import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  Language as LanguageIcon,
  Public as PublicIcon,
  Info as InfoIcon,
  Construction as ConstructionIcon
} from '@mui/icons-material';
import PageLayout from '../../components/Layout/PageLayout';

const TimeRangeSettingsPage: React.FC = () => {
  return (
    <PageLayout title="時間範圍設定">
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            時間範圍設定
          </Typography>
          <Typography variant="body1" color="text.secondary">
            配置系統時間範圍、時區和時間相關設定
          </Typography>
        </Box>

        {/* 即將推出提示 */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            功能開發中
          </Typography>
          <Typography variant="body2">
            時間範圍設定功能正在開發中，即將推出。此功能將包括：
          </Typography>
          <List dense sx={{ mt: 1 }}>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• 系統時區設定"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• 工作時間範圍配置"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• 日期格式設定"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• 時間同步設定"
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </ListItem>
          </List>
        </Alert>

        {/* 功能預覽卡片 */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ScheduleIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                  <Typography variant="h6">
                    時區設定
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  配置系統時區和本地時間設定
                </Typography>
                <Chip
                  label="即將推出"
                  color="warning"
                  size="small"
                  icon={<ConstructionIcon />}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TimeIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                  <Typography variant="h6">
                    工作時間
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  設定系統工作時間範圍和休息時間
                </Typography>
                <Chip
                  label="即將推出"
                  color="warning"
                  size="small"
                  icon={<ConstructionIcon />}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LanguageIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                  <Typography variant="h6">
                    日期格式
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  自定義日期和時間顯示格式
                </Typography>
                <Chip
                  label="即將推出"
                  color="warning"
                  size="small"
                  icon={<ConstructionIcon />}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PublicIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                  <Typography variant="h6">
                    時間同步
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  與外部時間服務器同步時間
                </Typography>
                <Chip
                  label="即將推出"
                  color="warning"
                  size="small"
                  icon={<ConstructionIcon />}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 當前系統時間信息 */}
        <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            當前系統時間
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" color="primary">
                  {new Date().toLocaleDateString('zh-HK')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  當前日期
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" color="primary">
                  {new Date().toLocaleTimeString('zh-HK')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  當前時間
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" color="primary">
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  系統時區
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" color="primary">
                  UTC+8
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  時區偏移
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </PageLayout>
  );
};

export default TimeRangeSettingsPage;
